import { NgOptimizedImage } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@pozi/authmodule';
import { OAuthService } from 'angular-oauth2-oidc';
import { EMPTY, switchMap } from 'rxjs';
import { environment } from '../../environments/environment';
import { AppInfoComponent } from '../main/app-info/app-info.component';
import { CompanyUser, ICompanyUser } from '../models/company-user';
import { CompanySiteService } from '../services/company-site.service';
import { LanguageSwitchComponent } from '../shared/language/language-switch/language-switch.component';
import { SharedVariablesService } from '../shared/shared-variables/shared-variables.service';
import {
  isLoginAttemptResponse,
  LoginAttemptResponse,
} from './models/logint-attempt-response.interface';

@Component({
  selector: 'pozi-login',
  imports: [
    ReactiveFormsModule,
    NgOptimizedImage,
    LanguageSwitchComponent,
    AppInfoComponent,
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  private authService =
    inject<AuthService<CompanyUser, ICompanyUser>>(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private oauthService = inject(OAuthService);
  private sharedVariablesService = inject(SharedVariablesService);
  private companySiteService = inject(CompanySiteService);
  private destroyRef = inject(DestroyRef);

  private redirect?: string;

  /**
   * OAuth providers offered on the login form. Deliberately a plain field: it is
   * assigned synchronously in `ngOnInit` from `SharedVariablesService`, not from
   * an async callback, so it is already in place before the first render.
   */
  oauthProviders?: {
    issuer: string;
    clientId: string;
    scope: string;
    label: string;
  }[];

  formGroup = new FormGroup({
    username: new FormControl<string>('', {
      validators: [Validators.email, Validators.required],
      nonNullable: true,
    }),
    password: new FormControl<string>('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
  });
  /**
   * Replaces the credentials form with the corporate-sign-in failure panel. Set
   * from the OAuth discovery/login error callback and from the token-exchange
   * error handler — both async, hence a signal.
   */
  displayOauthError = signal(false);

  loginAttemptResponse = signal<LoginAttemptResponse | null>(null);

  ngOnInit() {
    this.route.queryParamMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((qPM) => {
        if (qPM.has('origin')) {
          this.redirect = qPM.get('origin')!;
        }
      });
    if (this.sharedVariablesService.hasVar('oauthProviders')) {
      this.oauthProviders =
        this.sharedVariablesService.getVar('oauthProviders');
      if (
        this.oauthProviders &&
        this.oauthProviders.length &&
        sessionStorage.getItem('selectedOauthProviderIdx') !== null
      ) {
        this.initOAuth(
          Number.parseInt(sessionStorage.getItem('selectedOauthProviderIdx')!)
        );
      }
    }
  }

  login() {
    if (this.formGroup.valid) {
      this.authService
        .login(this.formGroup.getRawValue())
        .pipe(
          switchMap((user: ICompanyUser) =>
            user !== null
              ? this.companySiteService.validateCompanySiteCookies()
              : EMPTY
          ),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: () => {
            this.router.navigateByUrl(this.redirect || '');
            this.redirect = '';
          },
          error: (error: HttpErrorResponse) => {
            if (error.status === 401) {
              this.formGroup.setErrors({ invalidCreds: true });
              if (error.error && isLoginAttemptResponse(error.error)) {
                this.loginAttemptResponse.set(error.error);
              }
            } else {
              this.formGroup.setErrors({ loginFailed: true });
            }
          },
        });
    }
  }

  oauthLogin(providerIdx: number): void {
    if (this.oauthProviders && this.oauthProviders.length) {
      this.initOAuth(providerIdx);
      this.oauthService.initCodeFlow('/login');
    }
  }

  private initOAuth(idx: number) {
    if (this.oauthProviders && this.oauthProviders.length) {
      sessionStorage.setItem('selectedOauthProviderIdx', String(idx));
      this.oauthService.configure({
        issuer: this.oauthProviders[idx].issuer,
        clientId: this.oauthProviders[idx].clientId,
        scope: this.oauthProviders[idx].scope,
        redirectUri: window.location.origin + '/login',
        responseType: 'code',
        strictDiscoveryDocumentValidation: false,
        showDebugInformation: !environment.production,
      });
      this.oauthService
        .loadDiscoveryDocumentAndTryLogin({
          onLoginError: (err) => {
            console.error(err);
            this.displayOauthError.set(true);
          },
        })
        .then(() => {
          if (this.oauthService.hasValidAccessToken()) {
            this.authService
              .loginWithOAuthToken(
                this.oauthService.getAccessToken(),
                '/oauth2/token'
              )
              .subscribe({
                next: (user: ICompanyUser) => {
                  if (user !== null) {
                    this.router.navigateByUrl(this.redirect || '');
                    this.redirect = '';
                  }
                },
                error: (error: HttpErrorResponse) => {
                  console.error(error);
                  // The credentials form is not on screen at this point, so a
                  // form-level error key would render nothing (the template reads
                  // only `invalidCreds` and `loginFailed`). This is the corporate
                  // sign-in flow failing, which is exactly what the OAuth error
                  // panel says.
                  this.displayOauthError.set(true);
                },
              });
          }
        });
    }
  }

  protected readonly environment = environment;
}
